// import type { AppBootstrapOptions } from 'owa-app-module/lib/types/AppBootstrapOptions';
import type { NormalizedCacheObject, InMemoryCache, ApolloClient } from '@apollo/client';
import type { BootstrapOptions } from '../types/BootstrapOptions';

import { Module } from 'owa-workloads';
import { isBootFeatureEnabled } from 'owa-metatags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

import { lazyBootstrapAppBarCache } from 'owa-appbar-bootstrap';
import { M365ApplicationTypePolicies } from 'owa-appbar-typepolicies';

/**
 * Bootstraps apollo cache promise
 * @param apolloClient
 * @param options Bootstrap options
 * @returns Apollo Client
 */
export async function bootstrapApolloCachePromise(
    apolloClient: ApolloClient<NormalizedCacheObject>,
    options: BootstrapOptions
): Promise<ApolloClient<NormalizedCacheObject>> {
    // We need to cast it here to avoid a circular dep with owa-app-module's
    // AppBootstrapOptions declaration, which extends this module's BootstrapOptions.
    type AppBootstrapOptions = BootstrapOptions & {
        appModuleProps: { module: string };
    };
    const currentModule: string | undefined = (options as AppBootstrapOptions).appModuleProps
        ?.module;

    if (currentModule && shouldPrimeAppBarCache(currentModule)) {
        // Primes AppBar's static data and cache policies
        (apolloClient.cache as InMemoryCache).policies.addTypePolicies(M365ApplicationTypePolicies);
        await lazyBootstrapAppBarCache.importAndExecute(apolloClient);
    }

    return apolloClient;
}

/**
 * Determines whether app bar is enabled for a given module
 * @param currentModule
 * @returns true if app bar enabled
 */
function shouldPrimeAppBarCache(currentModule: string): boolean {
    const appBarEnabled = [
        Module.Mail,
        Module.Calendar,
        Module.AppHost,
        Module.People,
        Module.FilesHub,
    ];

    const isAppBarEnabledForModule = appBarEnabled.indexOf(currentModule as Module) !== -1;

    return (
        !isHostAppFeatureEnabled('hideOfficeRail') &&
        isBootFeatureEnabled('novaappbar') &&
        isAppBarEnabledForModule
    );
}
